import { Link, Outlet, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'
import { MY_TASKS_KEY } from '@witty-board/shared'
import { useProjectsQuery } from '../api/projects.js'
import { TaskBoard } from '../features/board/TaskBoard.js'
import { BoardSkeleton } from '../features/board/BoardSkeleton.js'
import { ProjectSwatch } from '../components/ProjectSwatch.js'
import { useDoneWindow } from '../lib/done-window.js'
import { HomePage } from './HomePage.js'

/** Resuelve `:key` de la URL a un proyecto: la clave, no el id, vive en la ruta (CLAUDE.md §6). */
export function ProjectBoardPage() {
  const { key } = useParams<{ key: string }>()
  const { t } = useTranslation()
  const projects = useProjectsQuery()
  const { doneSince } = useDoneWindow()

  // `MY_TASKS_KEY` no es un proyecto real: es la clave reservada que usa "Mis
  // tareas" para que su propia ruta de tarea (`/projects/TASKS/tasks/:id`)
  // exista sin depender de ningún estado de navegación efímero — un enlace
  // pegado o un refresco reconstruyen el mismo fondo sin más mecanismo.
  if (key === MY_TASKS_KEY) {
    return (
      <>
        <HomePage />
        <Outlet />
      </>
    )
  }

  if (projects.isPending) return <BoardSkeleton />

  const project = projects.data?.find((candidate) => candidate.key === key)

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 px-6 py-24 text-center">
        <p className="text-body text-text">{t('board.notFound.title')}</p>
        <Link to="/" className="text-body text-text underline hover:text-text-muted">
          {t('board.notFound.back')}
        </Link>
      </div>
    )
  }

  const projectsById = new Map([[project.id, project]])

  return (
    <div className="flex flex-col">
      <header className="flex shrink-0 items-center justify-between gap-2 px-6 pt-6">
        <div className="flex items-center gap-2">
          <ProjectSwatch color={project.color} />
          <h1 className="text-title font-semibold text-text">{project.name}</h1>
        </div>
        <Link
          to="trash"
          className="flex h-8 max-md:h-11 items-center gap-2 rounded-card px-2 text-meta text-text-muted hover:bg-surface-hi hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Trash2 className="h-3 w-3" aria-hidden="true" />
          {t('trash.title')}
        </Link>
      </header>
      <TaskBoard
        filters={{ projectId: project.id, doneSince }}
        projectsById={projectsById}
        boardKey={project.key}
        quickAddProjectId={project.id}
      />

      {/* Rutas anidadas `tasks/:id` y `trash`: el panel de detalle y la
          papelera se deslizan sobre el tablero sin taparlo, así que van aquí y
          no en una página aparte (CLAUDE.md §6, ver docs/DECISIONS.md fase 4). */}
      <Outlet />
    </div>
  )
}
